/* global BigInt */
import {makeAutoObservable, observable} from 'mobx';

const unavailable = 'Не удалось обновить предзаказ. Попробуйте ещё раз.';
const savingUnknown = 'Не удалось получить подтверждение. Повторное нажатие проверит этот же заказ — новый не создастся.';
const fail = () => {throw Error(unavailable);};
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const uuid = value => typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const hash = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const exact = (value, keys) => value && [Object.prototype, null].includes(Object.getPrototypeOf(value)) &&
  Reflect.ownKeys(value).length === keys.length && keys.every(key => {
    const d = Object.getOwnPropertyDescriptor(value, key); return d?.enumerable && Object.hasOwn(d, 'value');
  });
const text = (value, max) => typeof value === 'string' && value.length > 0 && value.length <= max && value.trim() === value && !/[\u0000-\u001f\u007f-\u009f]/.test(value);
const iso = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
const eta = value => exact(value, ['from', 'to']) && iso(value.from) && iso(value.to) && Date.parse(value.from) >= Date.now() && Date.parse(value.to) >= Date.parse(value.from);
const minor = value => typeof value === 'string' && /^(?:0|[1-9][0-9]{0,15})$/.test(value) && BigInt(value) <= BigInt(Number.MAX_SAFE_INTEGER);
const clone = value => JSON.parse(JSON.stringify(value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const sku = row => `${row.childProductId}:${row.colorId}:${row.sizeId}`;
export const choiceKey = choice => JSON.stringify([choice.deliveryId, choice.kind, choice.countryCode, choice.city, choice.pickupPoint,
  choice.paymentMode, choice.paymentMethodId, choice.cdek]);
export const deliveryKey = choice => JSON.stringify([choice.deliveryId, choice.kind, choice.countryCode, choice.city, choice.pickupPoint, choice.cdek]);
export const formatPreorderMoney = value => minor(value) ? `${(Number(value) / 100).toLocaleString('ru-RU', {minimumFractionDigits: 0, maximumFractionDigits: 2})} ₽` : '—';
export const formatPreorderDate = value => iso(value) ? new Intl.DateTimeFormat('ru-RU', {timeZone: 'Europe/Moscow'}).format(new Date(value)) : '—';

export function readCartOptions(raw, selected) {
  const target = selected.identity;
  const carrierAware = raw?.schemaVersion === 'confirmed-preorder-cart-options-v1';
  if (!exact(raw, ['schemaVersion', 'line', 'offerId', 'sizeName', 'maxQuantity', 'dispatchEta', 'choices', ...(carrierAware ? ['carriers'] : [])]) ||
    !carrierAware && raw.schemaVersion !== 'confirmed-preorder-options-v1' ||
    !exact(raw.line, ['childProductId', 'colorId', 'sizeId', 'batchId', 'categoryPath']) ||
    !['childProductId', 'colorId', 'sizeId'].every(key => id(raw.line[key]) && raw.line[key] === target[key]) ||
    raw.line.categoryPath !== null || !uuid(raw.line.batchId) || !text(raw.offerId, 255) || !text(raw.sizeName, 128) ||
    !id(raw.maxQuantity) || !eta(raw.dispatchEta) || !Array.isArray(raw.choices) || raw.choices.length > 256) fail();
  if (carrierAware && (!Array.isArray(raw.carriers) || raw.carriers.length > 20 || raw.carriers.some(row =>
    !exact(row, ['deliveryId', 'kind', 'name']) || !id(row.deliveryId) || row.kind !== 'cdek' || !text(row.name, 255)) ||
    new Set(raw.carriers.map(row => row.deliveryId)).size !== raw.carriers.length)) fail();
  if (!raw.choices.length && !raw.carriers?.length) fail();
  const seen = new Set();
  for (const choice of raw.choices) {
    if (!exact(choice, ['deliveryId', 'kind', 'countryCode', 'city', 'pickupPoint', 'paymentMode', 'paymentMethodId']) ||
      !id(choice.deliveryId) || !id(choice.paymentMethodId) || !['pickup', 'address'].includes(choice.kind) ||
      !['payment_now', 'payment_later'].includes(choice.paymentMode) || !/^[A-Z]{2}$/.test(choice.countryCode) || !text(choice.city, 255)) fail();
    if (choice.kind === 'pickup' ? !exact(choice.pickupPoint, ['id', 'name', 'address']) || !id(choice.pickupPoint.id) ||
      !text(choice.pickupPoint.name, 255) || !text(choice.pickupPoint.address, 2048) : choice.pickupPoint !== null) fail();
    const key = choiceKey(choice); if (seen.has(key)) fail(); seen.add(key);
  }
  return clone(raw);
}

export function commonChoices(options) {
  if (!options.length) return [];
  return options[0].choices.filter(choice => options.every(row => row.choices.some(other => choiceKey(other) === choiceKey(choice))));
}

export function readCartQuote(raw, options, draft) {
  if (!exact(raw, ['schemaVersion', 'termsSha256', 'paymentMode', 'currency', 'lines', 'merchandiseAmountMinor', 'deliveryAmountMinor', 'totalAmountMinor', 'holdTtlMs']) ||
    raw.schemaVersion !== 'confirmed-preorder-quote-v1' || !hash(raw.termsSha256) || raw.paymentMode !== draft.paymentMode || raw.currency !== 'RUB' ||
    !Array.isArray(raw.lines) || raw.lines.length !== draft.lines.length ||
    !['merchandiseAmountMinor', 'deliveryAmountMinor', 'totalAmountMinor'].every(key => minor(raw[key])) ||
    (draft.paymentMode === 'payment_now' ? !id(raw.holdTtlMs) : raw.holdTtlMs !== null)) fail();
  const seen = new Set(); let total = 0n;
  for (const row of raw.lines) {
    if (!exact(row, ['childProductId', 'colorId', 'sizeId', 'offerId', 'sizeName', 'quantity', 'unitAmountMinor', 'lineAmountMinor', 'dispatchEta'])) fail();
    const selected = draft.lines.find(item => sku(item) === sku(row));
    const option = options.find(item => sku(item.line) === sku(row));
    if (!selected || !option || seen.has(sku(row)) || row.quantity !== selected.quantity || !id(row.quantity) ||
      row.offerId !== option.offerId || row.sizeName !== option.sizeName || !eta(row.dispatchEta) ||
      !minor(row.unitAmountMinor) || BigInt(row.unitAmountMinor) === 0n || !minor(row.lineAmountMinor) ||
      BigInt(row.lineAmountMinor) !== BigInt(row.unitAmountMinor) * BigInt(row.quantity)) fail();
    seen.add(sku(row)); total += BigInt(row.lineAmountMinor);
  }
  if (total !== BigInt(raw.merchandiseAmountMinor) || BigInt(raw.totalAmountMinor) !== total + BigInt(raw.deliveryAmountMinor)) fail();
  return clone(raw);
}

function readReceipt(raw, expected) {
  if (!exact(raw, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'acceptedAt', 'termsSha256', 'currency', 'totalAmountMinor', 'paymentMode', 'paymentMethodId', 'paymentState']) ||
    raw.schemaVersion !== 'confirmed-preorder-checkout-v1' || raw.outcome !== 'accepted' || !id(raw.orderId) || !id(raw.orderNumber) ||
    !iso(raw.acceptedAt) || raw.termsSha256 !== expected.termsSha256 || !hash(raw.termsSha256) || raw.currency !== 'RUB' ||
    !minor(raw.totalAmountMinor) || raw.totalAmountMinor !== expected.totalAmountMinor || raw.paymentMode !== expected.paymentMode ||
    raw.paymentMethodId !== expected.paymentMethodId || raw.paymentState !== 'unpaid') fail();
  return clone(raw);
}

export function freshKey() {
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const h = Array.from(bytes, n => n.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Cart-only coordinator. Options and quotes grant neither stock nor payment;
// only the authenticated acceptance receipt may consume this draft selection.
export default class PreorderCheckout {
  options = []; choices = []; quote = null; command = null; selection = null; pending = null;
  receipt = null; receiptKey = null; autoStartPayment = false; state = 'idle'; error = ''; sequence = 0; disposed = false;
  marker = null; retryKey = null; active = false; privacy = false;
  carrierSelection = null;
  deliveryCity = undefined; // Checkout-owner scoped; never copied into the ordinary delivery store.
  constructor({cart, api, storage = window.localStorage, getToken = () => window.localStorage.getItem('token'), newKey = freshKey}) {
    this.cart = cart; this.api = api; this.storage = storage; this.getToken = getToken; this.newKey = newKey;
    this.owner = cart.captureSelection(); this.token = getToken();
    this.slot = this.owner.authScope === 'authenticated' && id(this.owner.principalId) ? `yw.preorder.cart-checkout.v1:${this.owner.principalId}` : null;
    makeAutoObservable(this, {cart: false, api: false, storage: false, getToken: false, newKey: false, owner: false, token: false,
      options: observable.ref, choices: observable.ref, quote: observable.ref, command: observable.ref, selection: observable.ref,
      pending: observable.ref, receipt: observable.ref, carrierSelection: observable.ref, deliveryCity: observable.ref}, {autoBind: true});
  }
  patch(value) {Object.assign(this, value);}
  current() {return !this.disposed && this.slot !== null && this.token !== null && this.token === this.getToken() &&
    this.cart.epoch === this.owner.epoch && this.cart.session?.authScope === this.owner.authScope && this.cart.session?.principalId === this.owner.principalId;}
  get locked() {return this.active || this.pending !== null || this.receipt !== null;}
  get carriers() {return (this.options[0]?.carriers || []).filter(row => this.options.every(option =>
    option.carriers?.some(other => same(row, other))));}
  rememberDeliveryCity(value) {
    if (!this.current() || this.locked) return false;
    this.deliveryCity = value ? clone(value) : null; return true;
  }
  clearCarrier() {
    if (this.locked) return false;
    this.invalidate(); this.patch({carrierSelection: null, choices: commonChoices(this.options)}); return true;
  }
  setCarrierSelection(value) {
    if (!this.current() || this.locked || !same(this.selection, this.cart.captureSelection()) ||
      !this.carriers.some(row => row.deliveryId === value.deliveryId) || Date.parse(value.expiresAt) <= Date.now()) return false;
    const choices = value.paymentChoices.map(payment => ({...payment, deliveryId: value.deliveryId, kind: 'cdek',
      countryCode: value.location.countryCode, city: value.location.city, pickupPoint: null,
      cdek: {schemaVersion: 'cdek-pvz-v1', pointCode: value.point.pointCode, quoteId: value.quoteId}}));
    this.invalidate(); this.patch({carrierSelection: clone(value), choices: [...commonChoices(this.options), ...choices]}); return true;
  }
  dispose() {this.disposed = true; this.sequence++;}
  invalidate() {
    if (this.pending || this.receipt) return false;
    this.sequence++; this.patch({quote: null, command: null, error: '', state: 'ready'}); return true;
  }
  readPending(raw) {
    const p = JSON.parse(raw);
    if (!exact(p, ['version', 'principalId', 'idempotencyKey', 'termsSha256', 'totalAmountMinor', 'paymentMode', 'paymentMethodId', 'selection']) ||
      p.version !== 1 || p.principalId !== this.owner.principalId || !uuid(p.idempotencyKey) || !hash(p.termsSha256) || !minor(p.totalAmountMinor) ||
      !['payment_now', 'payment_later'].includes(p.paymentMode) || !id(p.paymentMethodId) ||
      !exact(p.selection, ['epoch', 'authScope', 'principalId', 'lines']) || p.selection.authScope !== 'authenticated' ||
      p.selection.principalId !== p.principalId || !Array.isArray(p.selection.lines) || !p.selection.lines.length || p.selection.lines.length > 100) fail();
    const seen = new Set();
    for (const line of p.selection.lines) {
      if (!exact(line, ['lineKey', 'identity', 'quantity', 'revision']) || !text(line.lineKey, 128) ||
        !line.identity || !['childProductId', 'colorId', 'sizeId'].every(key => id(line.identity[key])) ||
        !id(line.quantity) || !id(line.revision) || seen.has(line.lineKey)) fail();
      seen.add(line.lineKey);
    }
    return p;
  }
  async load() {
    if (!this.current() || this.active || this.pending || this.receipt) return false;
    const revision = ++this.sequence; this.patch({state: 'loading', error: '', quote: null, command: null, options: [], choices: [], carrierSelection: null});
    try {
      const saved = this.storage.getItem(this.slot);
      if (saved !== null && saved !== this.marker) {
        const value = this.readPending(saved); this.patch({marker: saved, pending: {metadata: value, command: null}});
        return this.recover();
      }
      const selection = this.cart.captureSelection();
      if (!selection.lines.length || selection.lines.length > 100) {this.patch({options: [], choices: [], state: 'ready'}); return true;}
      const result = await Promise.all(selection.lines.map(async line => readCartOptions(await this.api.options({
        childProductId: line.identity.childProductId, colorId: line.identity.colorId, sizeId: line.identity.sizeId, categoryPath: null}), line)));
      if (!this.current() || revision !== this.sequence || !same(selection, this.cart.captureSelection())) return false;
      const choices = commonChoices(result);
      if (!choices.length && !(result[0].carriers || []).some(row => result.every(option => option.carriers?.some(other => same(row, other))))) fail();
      this.patch({selection, options: result, choices, state: 'ready'}); return true;
    } catch (error) {if (this.current() && revision === this.sequence) this.patch({state: 'error', error: unavailable}); return false;}
  }
  async calculate({choice, recipient, address, agreePrivacy}) {
    if (!this.current() || this.locked) return false;
    this.invalidate(); const revision = this.sequence;
    try {
      this.privacy = agreePrivacy === true;
      if (!choice || !this.choices.some(row => choiceKey(row) === choiceKey(choice)) ||
        !same(this.selection, this.cart.captureSelection())) return false;
      const telephone = String(recipient?.telephone || '').trim().replace(/[()\s-]/g, '');
      const phone = /^8\d{10}$/.test(telephone) ? '+7' + telephone.slice(1) : telephone;
      const r = {name: String(recipient?.name || '').trim(), surname: recipient?.surname?.trim() || null,
        patronymic: recipient?.patronymic?.trim() || null, telephone: phone, email: recipient?.email?.trim() || null};
      if (!text(r.name, 255) || !/^\+[1-9][0-9]{7,14}$/.test(r.telephone) || !r.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email) ||
        ['surname', 'patronymic'].some(key => r[key] !== null && !text(r[key], 255))) return false;
      const lines = this.selection.lines.map((line, index) => {
        if (!id(line.quantity) || line.quantity > this.options[index].maxQuantity) fail();
        return {...this.options[index].line, quantity: line.quantity};
      });
      let delivery = {deliveryId: choice.deliveryId, pickupPointId: choice.pickupPoint?.id};
      if (choice.kind === 'cdek') {
        if (!this.carrierSelection || this.carrierSelection.quoteId !== choice.cdek?.quoteId || Date.parse(this.carrierSelection.expiresAt) <= Date.now()) return false;
        delivery = {deliveryId: choice.deliveryId, cdek: clone(choice.cdek)};
      }
      if (choice.kind === 'address') {
        if (!text(address?.street?.trim(), 255) || !text(address?.house?.trim(), 32)) return false;
        delivery = {deliveryId: choice.deliveryId, address: {countryCode: choice.countryCode, city: choice.city,
          street: address.street.trim(), house: address.house.trim(), flat: address.flat?.trim() || null,
          front_door: null, floor: null, intercom: null}};
      }
      const draft = {idempotencyKey: this.retryKey || this.newKey(), paymentMode: choice.paymentMode, paymentMethodId: choice.paymentMethodId, lines, delivery, recipient: r};
      this.patch({state: 'quoting'});
      const quoted = readCartQuote(await this.api.quote(draft), this.options, draft);
      if (!this.current() || revision !== this.sequence || !same(this.selection, this.cart.captureSelection())) return false;
      this.patch({quote: quoted, command: {...draft, expectedTermsSha256: quoted.termsSha256}, state: 'ready'}); return true;
    } catch (_) {if (this.current() && revision === this.sequence) this.patch({state: 'error', error: unavailable}); return false;}
  }
  remember() {
    const metadata = {version: 1, principalId: this.owner.principalId, idempotencyKey: this.command.idempotencyKey,
      termsSha256: this.quote.termsSha256, totalAmountMinor: this.quote.totalAmountMinor, paymentMode: this.command.paymentMode,
      paymentMethodId: this.command.paymentMethodId, selection: clone(this.selection)};
    const serialized = JSON.stringify(metadata);
    if (this.storage.getItem(this.slot) !== this.marker) fail();
    this.storage.setItem(this.slot, serialized); if (this.storage.getItem(this.slot) !== serialized) fail();
    this.patch({marker: serialized, pending: {metadata, command: clone(this.command)}});
  }
  accepted(raw, fresh) {
    const pending = this.pending; const value = readReceipt(raw, pending.metadata);
    if (!this.current()) return false;
    const selection = clone(pending.metadata.selection);
    // A restored operation can consume only the exact persisted revisions. New
    // rows and changed rows remain in the draft; the store also fences owner.
    if (selection.epoch !== this.owner.epoch) {
      selection.epoch = this.owner.epoch;
      selection.lines = selection.lines.filter(line => this.cart.lines.some(current => current.lineKey === line.lineKey &&
        current.revision === line.revision && same(current.identity, line.identity) && current.quantity >= line.quantity));
    }
    const consumed = this.cart.removeAcceptedLines(selection);
    // Cleanup failure cannot undo an authoritative receipt or consume twice.
    try {if (consumed !== false && this.storage.getItem(this.slot) === this.marker) this.storage.removeItem(this.slot);} catch (_) { /* Recoverable, non-authority marker. */ }
    this.patch({receipt: value, receiptKey: pending.metadata.idempotencyKey, autoStartPayment: fresh && value.paymentMode === 'payment_now',
      pending: null, marker: null, state: 'accepted', error: ''}); return true;
  }
  async recover() {
    if (!this.current() || !this.pending || this.active) return false;
    let reloadOptions = false;
    this.patch({active: true, state: 'saving', error: ''});
    try {
      const result = await this.api.recover({idempotencyKey: this.pending.metadata.idempotencyKey});
      if (!this.current()) return false;
      if (exact(result, ['schemaVersion', 'outcome', 'retryWithSameKey']) && result.schemaVersion === 'confirmed-preorder-checkout-v1' &&
        result.outcome === 'not_found' && result.retryWithSameKey === true) {
        if (this.pending.command) this.patch({state: 'retry', error: 'Заказ ещё не оформлен. Повторите нажатие.'});
        else {this.patch({retryKey: this.pending.metadata.idempotencyKey, pending: null, state: 'ready', error: ''}); reloadOptions = true;}
        return false;
      }
      return this.accepted(result, false);
    } catch (_) {if (this.current()) this.patch({state: 'unknown', error: savingUnknown}); return false;}
    finally {this.patch({active: false}); if (reloadOptions && this.current()) await this.load();}
  }
  async submit() {
    if (!this.current() || this.active || this.receipt) return false;
    if (this.pending && this.state !== 'retry') return this.recover();
    if (!this.pending && this.command?.delivery.cdek && (!this.carrierSelection || Date.parse(this.carrierSelection.expiresAt) <= Date.now())) {
      this.clearCarrier(); return false;
    }
    if (!this.pending && (!this.privacy || !this.command || !this.quote || !same(this.selection, this.cart.captureSelection()))) return false;
    try {
      if (!this.pending) this.remember();
      if (!this.pending.command || this.storage.getItem(this.slot) !== this.marker) fail();
      this.patch({active: true, state: 'saving', error: ''});
      const raw = await this.api.accept(clone(this.pending.command));
      return this.accepted(raw, true);
    } catch (_) {
      this.patch({active: false});
      if (this.current() && this.pending) return this.recover();
      if (this.current()) this.patch({state: 'error', error: unavailable}); return false;
    } finally {this.patch({active: false});}
  }
}
